import { fetchAllJsonCached } from '../../fetch-all-json';

const ENGINES = [
  ['google', 'Google', 'go_vote_metrics'],
  ['bing', 'Bing', 'go_vote_metrics_bing'],
  ['yahoo', 'Yahoo', 'go_vote_metrics_yahoo']
];
const GROUPS = [['c_bias', 'Conservative', '#b02919'], ['l_bias', 'Liberal', '#3230cc'], ['s_bias', 'Moderate / Other', '#7a36c3']];
const REGIONS = [['nationwide', 'Nationwide'], ['pennsylvania', 'Pennsylvania'], ['georgia', 'Georgia'], ['northCarolina', 'North Carolina'], ['michigan', 'Michigan'], ['arizona', 'Arizona'], ['wisconsin', 'Wisconsin'], ['nevada', 'Nevada']];
const PERIODS = [['monthly_bias_graph', 'Archive month (daily counts)'], ['weekly_bias_graph', 'Archive week (daily counts)'], ['daily_bias_graph', 'Archive day (hourly counts)']];
const validCount = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const count = value => validCount(value) ? value.toLocaleString('en-US') : '—';

function node (documentRoot, tag, text) {
  const result = documentRoot.createElement(tag);
  if (text !== undefined) result.textContent = text;
  return result;
}

function svgNode (documentRoot, tag, attributes = {}, text) {
  const result = documentRoot.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [key, value] of Object.entries(attributes)) result.setAttribute(key, value);
  if (text !== undefined) result.textContent = text;
  return result;
}

function dateValue (row) {
  const value = row.record_date;
  if (typeof value !== 'string') return NaN;
  return Date.parse(value.includes('T') ? value : value.replace(' ', 'T') + (value.length > 10 ? 'Z' : ''));
}

function renderFigure (documentRoot, engine, label, rows) {
  const figure = node(documentRoot, 'figure');
  figure.dataset.engine = engine;
  figure.append(node(documentRoot, 'h3', label));
  const ordered = Array.isArray(rows) ? rows.filter(row => Number.isFinite(dateValue(row))).sort((a, b) => dateValue(a) - dateValue(b)) : [];
  if (!ordered.length || !ordered.some(row => GROUPS.some(([key]) => validCount(row[key])))) {
    figure.append(node(documentRoot, 'p', 'Archived observations unavailable for this selection.'));
    return figure;
  }
  const start = dateValue(ordered[0]);
  const end = dateValue(ordered[ordered.length - 1]);
  const dateLabel = row => row.record_date.replace('T', ' ').replace(/Z$/, '');
  const title = `${label}: ${dateLabel(ordered[0])} to ${dateLabel(ordered[ordered.length - 1])}`;
  figure.append(node(documentRoot, 'figcaption', title));
  const svg = svgNode(documentRoot, 'svg', { viewBox: '0 0 640 240', role: 'img', 'aria-label': `${title}. Keyword-positive record counts; exact values in the data table below.` });
  svg.append(svgNode(documentRoot, 'title', {}, title));
  const maximum = Math.max(1, ...ordered.flatMap(row => GROUPS.map(([key]) => validCount(row[key]) ? row[key] : 0)));
  const x = row => 65 + (end === start ? 0.5 : (dateValue(row) - start) / (end - start)) * 530;
  const y = value => 185 - value / maximum * 155;
  for (const fraction of [0, 0.5, 1]) {
    const height = y(maximum * fraction);
    svg.append(svgNode(documentRoot, 'line', { x1: 65, x2: 595, y1: height, y2: height, stroke: '#d9dde4' }));
    svg.append(svgNode(documentRoot, 'text', { x: 58, y: height + 5, 'text-anchor': 'end' }, Math.round(maximum * fraction).toLocaleString('en-US')));
  }
  svg.append(svgNode(documentRoot, 'text', { x: 65, y: 18 }, 'Records'));
  svg.append(svgNode(documentRoot, 'text', { x: 65, y: 215 }, ordered[0].record_date.slice(0, 10)));
  svg.append(svgNode(documentRoot, 'text', { x: 595, y: 215, 'text-anchor': 'end' }, ordered[ordered.length - 1].record_date.slice(0, 10)));
  const legend = node(documentRoot, 'ul');
  legend.className = 'archive-chart-legend';
  GROUPS.forEach(([key, group, color], groupIndex) => {
    const dash = ['', '7 4', '2 4'][groupIndex];
    let points = [];
    const flush = () => {
      if (points.length) svg.append(svgNode(documentRoot, 'polyline', { points: points.join(' '), fill: 'none', stroke: color, 'stroke-width': 2.5, 'stroke-dasharray': dash }));
      points = [];
    };
    for (const row of ordered) {
      if (!validCount(row[key])) { flush(); continue; }
      points.push(`${x(row)},${y(row[key])}`);
      const point = svgNode(documentRoot, 'circle', { cx: x(row), cy: y(row[key]), r: 2.5, fill: color });
      point.append(svgNode(documentRoot, 'title', {}, `${group}, ${dateLabel(row)}: ${count(row[key])} records`));
      svg.append(point);
    }
    flush();
    const item = node(documentRoot, 'li', group);
    item.style.color = color;
    legend.append(item);
  });
  figure.append(svg, legend);
  const details = node(documentRoot, 'details');
  details.append(node(documentRoot, 'summary', `View ${label} data table`));
  const scroll = node(documentRoot, 'div');
  scroll.className = 'archive-table-scroll';
  scroll.tabIndex = 0;
  scroll.setAttribute('role', 'region');
  scroll.setAttribute('aria-label', `${label} archived record counts`);
  const table = node(documentRoot, 'table');
  table.append(node(documentRoot, 'caption', `${label}: keyword-positive records by self-reported group`));
  const head = node(documentRoot, 'thead');
  const headings = node(documentRoot, 'tr');
  for (const heading of ['Recorded date', ...GROUPS.map(([, group]) => group)]) {
    const cell = node(documentRoot, 'th', heading);
    cell.scope = 'col';
    headings.append(cell);
  }
  head.append(headings);
  const body = node(documentRoot, 'tbody');
  for (const row of ordered) {
    const tr = node(documentRoot, 'tr');
    const date = node(documentRoot, 'th', dateLabel(row));
    date.scope = 'row';
    tr.append(date);
    for (const [key] of GROUPS) tr.append(node(documentRoot, 'td', count(row[key])));
    body.append(tr);
  }
  table.append(head, body);
  scroll.append(table);
  details.append(scroll);
  figure.append(details);
  return figure;
}

export function renderArchiveCharts (container, payload) {
  const documentRoot = container.ownerDocument;
  container.classList.add('archive-keyword-charts');
  container.replaceChildren(node(documentRoot, 'h2', 'Archived homepage election-keyword observations'));
  container.append(node(documentRoot, 'p', 'Historical HTML observations, separate from screenshot OCR. These are keyword-positive record counts, not people, votes cast or unique exposures. Counts by self-reported political group are not normalized by group size and do not establish bias or displayed voting reminders. Dates reflect the archive, not current activity; aggregates may include zero-filled time bins.'));
  const controls = node(documentRoot, 'div');
  controls.className = 'archive-chart-controls';
  const select = (name, title, options) => {
    const label = node(documentRoot, 'label', title);
    label.htmlFor = name;
    const input = node(documentRoot, 'select');
    input.id = name;
    input.name = name;
    for (const [value, text] of options) {
      const option = node(documentRoot, 'option', text);
      option.value = value;
      input.append(option);
    }
    controls.append(label, input);
    return input;
  };
  const region = select('archive-region', 'Geography', REGIONS);
  const period = select('archive-period', 'Archive window', PERIODS);
  const charts = node(documentRoot, 'div');
  charts.className = 'archive-chart-grid';
  const status = node(documentRoot, 'p');
  status.setAttribute('role', 'status');
  const update = () => {
    charts.replaceChildren(...ENGINES.map(([engine, label, key]) => renderFigure(documentRoot, engine, label, payload?.[key]?.[region.value]?.[period.value])));
    status.textContent = `${region.selectedOptions[0].textContent}; ${period.selectedOptions[0].textContent}.`;
  };
  region.addEventListener('change', update);
  period.addEventListener('change', update);
  container.append(controls, status, charts);
  update();
}

export async function initArchiveCharts (load = fetchAllJsonCached, documentRoot = document) {
  const container = documentRoot.getElementById('archive-keyword-charts');
  if (!container) return;
  try {
    renderArchiveCharts(container, await load());
  } catch {
    renderArchiveCharts(container, null);
  }
}
